import axios from "axios";
import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import {
  NowPaymentsCreatePaymentUrl,
  NowPaymentsCurrenciesUrl,
} from "../../utils/Routes";

const formatCurrencyName = (currency) => {
  // Special cases for common cryptocurrencies
  const specialCases = {
    btc: "Bitcoin (BTC)",
    eth: "Ethereum (ETH)",
    usdt: "Tether (USDT)",
    usdc: "USD Coin (USDC)",
    bnb: "Binance Coin (BNB)",
    xrp: "Ripple (XRP)",
    ada: "Cardano (ADA)",
    sol: "Solana (SOL)",
    dot: "Polkadot (DOT)",
    doge: "Dogecoin (DOGE)",
  };

  if (specialCases[currency.toLowerCase()]) {
    return specialCases[currency.toLowerCase()];
  }

  // Handle suffixes
  const suffixes = ["erc20", "bsc", "arb", "matic", "mainnet", "trc20"];
  let base = currency;
  let suffix = "";

  for (const s of suffixes) {
    if (currency.toLowerCase().endsWith(s)) {
      base = currency.slice(0, -s.length);
      suffix = ` (${s.toUpperCase()})`;
      break;
    }
  }

  // Capitalize the base name
  base = base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
  return base + suffix;
};

const CountdownTimer = ({ expiryDate }) => {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiryDate).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        return "Истекло";
      }

      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expiryDate]);

  return (
    <div className="text-center bg-gray-800 p-4 rounded-lg mb-4">
      <p className="text-gray-300 text-sm mb-1">Оплата истекает через:</p>
      <p className="text-xl font-mono font-bold text-white">{timeLeft}</p>
    </div>
  );
};

const PaymentDetailsModal = ({
  isOpen,
  onClose,
  paymentDetails,
  selectedCurrency,
}) => {
  const [currentStatus, setCurrentStatus] = useState(
    paymentDetails?.payment_status || "waiting"
  );
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    let ws;

    if (isOpen && paymentDetails?.payment_id) {
      console.log(
        `[WebSocket] Initializing connection for payment ${paymentDetails.payment_id}`
      );

      ws = new WebSocket(`wss://api.hydraflasher.com/payments`);

      ws.onopen = () => {
        console.log("[WebSocket] Connection established");
        const subscribeMsg = {
          type: "subscribe",
          payment_id: paymentDetails.payment_id,
        };
        console.log("[WebSocket] Subscribing to updates:", subscribeMsg);
        ws.send(JSON.stringify(subscribeMsg));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log("[WebSocket] Received message:", data);

        if (
          data.type === "payment_update" &&
          data.payment_id === paymentDetails.payment_id
        ) {
          console.log(`[Payment Status] Updated to: ${data.payment_status}`);
          setCurrentStatus(data.payment_status);

          if (data.payment_status === "finished") {
            setStatusMessage({
              type: "success",
              title: "Оплата успешна!",
              message:
                "Ваш платеж успешно обработан. Перенаправление на страницу успеха...",
            });
          } else if (["failed", "expired"].includes(data.payment_status)) {
            setStatusMessage({
              type: "error",
              title: `Оплата ${
                data.payment_status === "failed" ? "не удалась" : "истекла"
              }`,
              message:
                "Возникла проблема с обработкой вашего платежа. Пожалуйста, попробуйте еще раз.",
            });
            setTimeout(() => {
              onClose();
            }, 5000);
          }
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket Error:", error);
        setStatusMessage({
          type: "error",
          title: "Ошибка подключения",
          message:
            "Не удалось отследить статус платежа. Пожалуйста, свяжитесь с поддержкой, если вы уже отправили платеж.",
        });
      };

      ws.onclose = () => {
        console.log("WebSocket Disconnected");
      };
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [isOpen, paymentDetails?.payment_id]);

  const getStatusColor = (status) => {
    switch (status) {
      case "waiting":
        return "text-yellow-400";
      case "confirming":
        return "text-blue-400";
      case "confirmed":
        return "text-blue-500";
      case "sending":
        return "text-blue-600";
      case "finished":
        return "text-green-500";
      case "failed":
      case "expired":
        return "text-red-500";
      default:
        return "text-gray-400";
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case "waiting":
        return "Ожидание платежа";
      case "confirming":
        return "Платеж обнаружен, ожидание подтверждения";
      case "confirmed":
        return "Платеж подтвержден";
      case "sending":
        return "Обработка платежа";
      case "finished":
        return "Платеж успешно завершен";
      case "failed":
        return "Платеж не удался";
      case "expired":
        return "Платеж истек";
      default:
        return "Неизвестный статус";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#26303C] p-6 rounded-lg max-w-lg w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium text-white">Детали платежа</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {statusMessage && (
            <div
              className={`p-4 rounded-lg ${
                statusMessage.type === "success" ? "bg-green-800" : "bg-red-800"
              } text-white`}
            >
              <h4 className="font-medium text-lg mb-1">
                {statusMessage.title}
              </h4>
              <p className="text-sm opacity-90">{statusMessage.message}</p>
            </div>
          )}

          <CountdownTimer
            expiryDate={paymentDetails.expiration_estimate_date}
          />

          <div className="bg-gray-800 p-4 rounded-lg text-center">
            <p className="text-gray-300 text-sm mb-1">Статус платежа:</p>
            <p
              className={`text-lg font-medium ${getStatusColor(currentStatus)}`}
            >
              {getStatusMessage(currentStatus)}
            </p>
          </div>

          <div className="flex justify-between items-center bg-gray-700 p-4 rounded-lg">
            <div>
              <p className="text-gray-300 text-sm">Сумма к оплате:</p>
              <p className="text-white font-medium">
                {paymentDetails.pay_amount}
                {paymentDetails.pay_currency.toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-gray-300 text-sm">Цена:</p>
              <p className="text-white font-medium">
                ${paymentDetails.price_amount}{" "}
                {paymentDetails.price_currency.toUpperCase()}
              </p>
            </div>
          </div>

          <div className="bg-gray-700 p-4 rounded-lg">
            <p className="text-gray-300 text-sm mb-1">
              Отправьте платеж на этот адрес:
            </p>
            <p className="font-mono text-blue-400 break-all text-sm">
              {paymentDetails.pay_address}
            </p>
          </div>

          <div className="flex justify-center py-4">
            {paymentDetails.pay_address && (
              <QRCode
                value={paymentDetails.pay_address}
                size={180}
                level="H"
                className="bg-white p-2 rounded-lg"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions for payment tracking
const setPendingPayment = (paymentData) => {
  const pendingPayments = JSON.parse(
    localStorage.getItem("pendingPayments") || "[]"
  );
  pendingPayments.push({
    payment_id: paymentData.payment_id,
    created_at: new Date().toISOString(),
    status: paymentData.payment_status,
    expires_at: paymentData.expiration_estimate_date,
  });
  localStorage.setItem("pendingPayments", JSON.stringify(pendingPayments));
};

const removePendingPayment = (payment_id) => {
  const pendingPayments = JSON.parse(
    localStorage.getItem("pendingPayments") || "[]"
  );
  const filtered = pendingPayments.filter((p) => p.payment_id !== payment_id);
  localStorage.setItem("pendingPayments", JSON.stringify(filtered));
};

const PaymentStatusTracker = () => {
  useEffect(() => {
    const checkPendingPayments = async () => {
      const pendingPayments = JSON.parse(
        localStorage.getItem("pendingPayments") || "[]"
      );
      console.log(
        "[Payment Tracker] Checking pending payments:",
        pendingPayments
      );
      const now = new Date();

      for (const payment of pendingPayments) {
        // Skip expired payments
        if (new Date(payment.expires_at) < now) {
          console.log(
            `[Payment Tracker] Payment ${payment.payment_id} has expired, removing from pending`
          );
          removePendingPayment(payment.payment_id);
          continue;
        }

        try {
          console.log(
            `[Payment Tracker] Checking status for payment ${payment.payment_id}`
          );
          const response = await axios.get(
            `/api/nowpayments/payment-status/${payment.payment_id}`
          );
          const status = response.data.data.payment_status;
          console.log(
            `[Payment Tracker] Status received for ${payment.payment_id}:`,
            status
          );

          // If payment is in final state, remove from pending
          if (["finished", "failed", "expired", "refunded"].includes(status)) {
            console.log(
              `[Payment Tracker] Payment ${payment.payment_id} reached final state: ${status}`
            );
            removePendingPayment(payment.payment_id);

            // Handle completed payment
            if (status === "finished") {
              console.log(
                `[Payment Tracker] Payment ${payment.payment_id} completed successfully, redirecting to success page`
              );
              window.location.href = "/payment/success";
            }
          }
        } catch (error) {
          console.error(
            `[Payment Tracker] Error checking payment ${payment.payment_id}:`,
            error
          );
        }
      }
    };

    console.log("[Payment Tracker] Initializing payment status tracker");
    // Check every 30 seconds
    const intervalId = setInterval(checkPendingPayments, 30000);

    // Initial check
    checkPendingPayments();

    return () => {
      console.log("[Payment Tracker] Cleaning up payment status tracker");
      clearInterval(intervalId);
    };
  }, []);

  return null;
};

const NowPayments = ({ priceAfterVoucher, details, onPaymentCreated }) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [showCurrencySelection, setShowCurrencySelection] = useState(true);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [isFetchingCurrencies, setIsFetchingCurrencies] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCurrencies = async () => {
    console.log("[Currency Fetch] Starting to fetch available currencies");
    setFetchError(null);
    setIsFetchingCurrencies(true);
    try {
      const response = await axios.get(NowPaymentsCurrenciesUrl);
      console.log(
        "[Currency Fetch] Available currencies:",
        response.data.data.currencies
      );
      setAvailableCurrencies(response.data.data.currencies);
    } catch (error) {
      console.error("[Currency Fetch] Error:", error);
      setFetchError(error);
    } finally {
      setIsFetchingCurrencies(false);
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const filteredCurrencies = availableCurrencies.filter((currency) =>
    currency.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const createPayment = async (currency) => {
    console.log(
      `[Payment Creation] Starting payment creation for currency: ${currency}`
    );
    setLoading(true);
    try {
      const user = localStorage.getItem("user");
      if (!user) {
        console.error("[Payment Creation] User not logged in");
        throw new Error("Пользователь не авторизован");
      }

      const userInfo = JSON.parse(user);
      console.log("[Payment Creation] Creating payment with details:", {
        price: priceAfterVoucher,
        currency,
        product: details?.title,
        email: userInfo.email,
      });

      const response = await axios.post(NowPaymentsCreatePaymentUrl, {
        price: priceAfterVoucher,
        price_amount: priceAfterVoucher,
        price_currency: "USD",
        pay_currency: currency,
        product_name: details?.title,
        email: userInfo.email,
      });

      console.log(
        "[Payment Creation] Payment created successfully:",
        response.data
      );
      const paymentData = response.data.data;
      setPaymentDetails(paymentData);
      setShowPaymentDetails(true);

      // Call the callback with payment data
      if (onPaymentCreated && typeof onPaymentCreated === "function") {
        console.log(
          "[Payment Creation] Calling onPaymentCreated with payment data"
        );
        onPaymentCreated(paymentData);
      }

      // Close the currency selection modal
      setShowCurrencySelection(false);

      console.log(
        "[Payment Creation] Adding payment to pending list:",
        paymentData.payment_id
      );
      setPendingPayment(paymentData);
    } catch (error) {
      console.error("[Payment Creation] Error:", error);
      alert(`Ошибка платежа: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentStatusTracker />
      {showCurrencySelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#26303C] p-6 rounded-lg max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-medium text-white">
                {isFetchingCurrencies
                  ? "Загрузка валют..."
                  : "Выберите валюту платежа"}
              </h3>
              <button
                onClick={() => setShowCurrencySelection(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {!isFetchingCurrencies && !fetchError && (
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Поиск валют..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
                  />
                  <svg
                    className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            )}

            {fetchError ? (
              <div className="text-center py-4">
                <p className="text-red-400 mb-3">Не удалось загрузить валюты</p>
                <button
                  onClick={fetchCurrencies}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Повторить
                </button>
              </div>
            ) : isFetchingCurrencies ? (
              <div className="flex justify-center py-4">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {filteredCurrencies.map((currency) => (
                    <button
                      key={currency}
                      onClick={() => {
                        setSelectedCurrency(currency);
                        setShowCurrencySelection(false);
                        createPayment(currency);
                      }}
                      className="p-4 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center text-center"
                    >
                      {formatCurrencyName(currency)}
                    </button>
                  ))}
                </div>
                {filteredCurrencies.length === 0 && (
                  <p className="text-gray-400 text-center mt-4">
                    Не найдено валют, соответствующих вашему поиску
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <PaymentDetailsModal
        isOpen={showPaymentDetails}
        onClose={() => setShowPaymentDetails(false)}
        paymentDetails={paymentDetails}
        selectedCurrency={selectedCurrency}
      />
    </div>
  );
};

export default NowPayments;
